import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  Empty,
  Image,
  Popconfirm,
  Spin,
  Switch,
  Upload,
  message
} from 'antd'
import {
  CameraOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined
} from '@ant-design/icons'

import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { getApiErrorMessage } from '../utils/api'
import { PHOTO_URL } from '../utils/apiutils'
import {
  listServicePhotosByBooking,
  uploadServicePhotos,
  updateServicePhoto,
  deleteServicePhoto
} from '../api/servicePhoto'

/**
 * 預約詳情裡的「施作照片」區塊。
 *
 * 設計成自給自足：只吃 businessId / bookingId 兩個 props，
 * 方案沒開通就自己 return null，父層（BookingDetail）完全不用判斷 ——
 * 與 ConsentFormBookingSection 同一個模式。
 */

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e7;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 16px;

  .anticon {
    color: #d5728a;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`

const PhotoCard = styled.div`
  border: 1px solid #e5e5e7;
  border-radius: 10px;
  overflow: hidden;
  background: #fafafa;

  .thumb {
    width: 100%;
    aspect-ratio: 1 / 1;
    background: #f0f0f0;
  }

  .thumb .ant-image,
  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const PhotoMeta = styled.div`
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #86868b;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`

const Note = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  background: #fdf0f3;
  border: 1px solid #f4d6de;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.7;
  color: #8a5c68;
`

const toFullUrl = (path) => {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  return `${PHOTO_URL}${path}`
}

const ServicePhotoBookingSection = ({ businessId, bookingId }) => {
  const { hasFeature } = useMembership()
  const { can, canAny } = usePermission()

  const featureEnabled = hasFeature('servicePhoto')
  const canView = canAny(['servicePhoto:view', 'servicePhoto:manage'])
  const canManage = can('servicePhoto:manage')

  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [photos, setPhotos] = useState([])
  const [limit, setLimit] = useState(8)

  const fetchPhotos = useCallback(async () => {
    if (!businessId || !bookingId) return
    setLoading(true)
    try {
      const res = await listServicePhotosByBooking(businessId, bookingId)
      setPhotos(res?.data?.data || [])
      if (res?.data?.meta?.limit) setLimit(res.data.meta.limit)
    } catch (err) {
      // 靜默：方案未開通或權限不足時不需要在預約詳情跳錯誤
      setPhotos([])
    } finally {
      setLoading(false)
    }
  }, [businessId, bookingId])

  useEffect(() => {
    if (featureEnabled && canView) fetchPhotos()
  }, [featureEnabled, canView, fetchPhotos])

  const remaining = useMemo(
    () => Math.max(limit - photos.length, 0),
    [limit, photos.length]
  )

  /**
   * 一次最多送 5 張（後端 multer 的上限），超過就自動分批。
   * 店家從相簿一次選十幾張是常態，讓他自己分兩次選很不合理。
   */
  const handleUpload = async (fileList) => {
    if (!fileList.length) return
    if (remaining <= 0) {
      message.warning(`這筆預約最多 ${limit} 張照片`)
      return
    }

    const files = fileList.slice(0, remaining)
    if (files.length < fileList.length) {
      message.warning(
        `只剩 ${remaining} 個位置，超過的 ${fileList.length - files.length} 張未上傳`
      )
    }

    setUploading(true)
    try {
      for (let i = 0; i < files.length; i += 5) {
        await uploadServicePhotos(businessId, bookingId, files.slice(i, i + 5), {
          source: 'booking'
        })
      }
      message.success('照片已上傳，客人在預約紀錄就看得到了')
      await fetchPhotos()
    } catch (err) {
      message.error(getApiErrorMessage(err, '上傳照片失敗'))
    } finally {
      setUploading(false)
    }
  }

  const handleToggleVisible = async (photo, visible) => {
    // 先更新畫面再打 API：切換開關要立刻有反應，失敗再轉回來
    setPhotos((prev) =>
      prev.map((p) =>
        p._id === photo._id ? { ...p, visibleToCustomer: visible } : p
      )
    )
    try {
      await updateServicePhoto(businessId, photo._id, {
        visibleToCustomer: visible
      })
    } catch (err) {
      setPhotos((prev) =>
        prev.map((p) =>
          p._id === photo._id
            ? { ...p, visibleToCustomer: !visible }
            : p
        )
      )
      message.error(getApiErrorMessage(err, '更新失敗'))
    }
  }

  const handleDelete = async (photo) => {
    try {
      await deleteServicePhoto(businessId, photo._id)
      setPhotos((prev) => prev.filter((p) => p._id !== photo._id))
      message.success('已刪除')
    } catch (err) {
      message.error(getApiErrorMessage(err, '刪除失敗'))
    }
  }

  if (!featureEnabled || !canView) return null

  return (
    <Card>
      <Title>
        <CameraOutlined />
        施作照片
        <span style={{ flex: 1 }} />
        <Button
          size='small'
          icon={<ReloadOutlined />}
          onClick={fetchPhotos}
          loading={loading}
        />
        {canManage && (
          <Upload
            multiple
            accept='image/*'
            showUploadList={false}
            fileList={[]}
            beforeUpload={(file, batch) => {
              /**
               * antd 會對批次裡的每個檔案各呼叫一次 beforeUpload，
               * 但第二個參數拿到的是「整批」。所以只在最後一個檔案時才送出 ——
               * 否則一次選 3 張會觸發 3 次上傳、每次都送同樣的 3 張，
               * 結果就是每張各存了 3 份。
               */
              if (batch[batch.length - 1] === file) {
                handleUpload(batch)
              }
              return Upload.LIST_IGNORE
            }}
          >
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              loading={uploading}
              disabled={remaining <= 0}
            >
              上傳照片
            </Button>
          </Upload>
        )}
      </Title>

      {loading && photos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      ) : photos.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            canManage
              ? '這次施作還沒有照片，點右上「上傳照片」留下紀錄'
              : '這次施作還沒有照片'
          }
        />
      ) : (
        <Image.PreviewGroup>
          <Grid>
            {photos.map((photo) => (
              <PhotoCard key={photo._id}>
                <div className='thumb'>
                  <Image
                    src={toFullUrl(photo.previewUrl || photo.url)}
                    preview={{ src: toFullUrl(photo.url) }}
                    alt={photo.caption || '施作照片'}
                  />
                </div>
                <PhotoMeta>
                  <MetaRow>
                    <span>客人可見</span>
                    <Switch
                      size='small'
                      checked={photo.visibleToCustomer !== false}
                      disabled={!canManage}
                      onChange={(checked) => handleToggleVisible(photo, checked)}
                    />
                  </MetaRow>
                  <MetaRow>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {photo.uploadedByName || '—'}
                    </span>
                    {canManage && (
                      <Popconfirm
                        title='確定刪除這張照片？'
                        description='刪除後客人的預約紀錄也會看不到。'
                        okText='刪除'
                        cancelText='取消'
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(photo)}
                      >
                        <Button
                          size='small'
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    )}
                  </MetaRow>
                </PhotoMeta>
              </PhotoCard>
            ))}
          </Grid>
        </Image.PreviewGroup>
      )}

      <Note>
        客人在自己的預約紀錄裡只會看到「客人可見」有打開的照片，
        預約備註與結帳備註都不會給客人看到。
        {canManage && ` 這筆預約還可以再上傳 ${remaining} 張（上限 ${limit} 張）。`}
      </Note>
    </Card>
  )
}

export default ServicePhotoBookingSection
